import { UseAsyncResult, UseAsyncStatus } from '@shared/async/use-async';
import { useErrorHandler } from '@shared/errors';
import { Box, Text } from 'grommet';
import React, { useEffect } from 'react';

import { Busy } from '@/components/busy';
import { Dialog, DialogActions, DialogBody, DialogHeader } from '@/components/dialog';
import { Button } from '@/components-new/button';
import { notifySuccess } from '@/lib/notification/notifications';

type ConfirmationDialogProps<TExecuteParameters extends unknown[]> = {
  /**
   * Whether the dialog is open or not, required.
   */
  open: boolean;

  /**
   * The title to display in the dialog, required.
   */
  title: string;

  /**
   * The message to display in the dialog, required.
   */
  message: string;

  /**
   * The label for the cancel button, optional. Defaults to "Cancel".
   */
  cancelLabel?: string;

  /**
   * The label for the confirm button, optional. Defaults to "Confirm".
   */
  confirmLabel?: string;

  /**
   * The color of the confirm button, optional. Defaults to "red".
   */
  confirmColor?: string;

  /**
   * The item name for success/error messages, optional. 
   * If not provided, generic success/error messages will be used.
   */
  itemName?: string;

  /**
   * Success message to display after the action completes, optional.
   */
  successMessage?: string;

  /**
   * Success title to display after the action completes, optional.
   */
  successTitle?: string;

  /**
   * Cancellation handler, required.
   */
  onCancel: () => void;

  /**
   * Handler to call when the action is complete, required.
   */
  onActionComplete: () => void;

  /**
   * The request to perform the action, required.
   */
  request: UseAsyncResult<TExecuteParameters, unknown>;

  /**
   * The parameters to pass to the request, required.
   */
  parameters: TExecuteParameters;
};

/**
 * Generic confirmation dialog for various actions like deletion, cancellation, etc.
 */
export const ConfirmationDialog = <TExecuteParameters extends unknown[]>({ 
  open, 
  title, 
  message, 
  cancelLabel = 'Cancel', 
  confirmLabel = 'Confirm', 
  itemName = 'item', 
  successTitle = 'Action successful', 
  successMessage,
  onCancel, 
  onActionComplete, 
  request, 
  parameters 
}: ConfirmationDialogProps<TExecuteParameters>) => {
  const loading = request.status === UseAsyncStatus.Pending;
  const { handleError } = useErrorHandler();

  const defaultSuccessMessage = successMessage || 'Action completed successfully.';

  useEffect(() => {
    const { status, error } = request;
    if (status === UseAsyncStatus.Pending || status === UseAsyncStatus.Idle) return;

    if (status === UseAsyncStatus.Error) {
      handleError(error, { title: `Failed to perform action on ${itemName}` });
      return;
    }

    notifySuccess({ title: successTitle, message: defaultSuccessMessage });
    onActionComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.status]);

  return (
    <Dialog open={open} width="large">
      <DialogHeader title={title} onClose={onCancel}/>
      <DialogBody>
        <Box>
          <Text>{message}</Text>
        </Box>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onCancel} disabled={loading}>{cancelLabel}</Button>
        <Button
          color="red"
          onClick={() => request.execute(...parameters)}
          disabled={loading}
        >
          <Busy busy={loading} content={confirmLabel}/>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Specialized version of ConfirmationDialog for delete operations.
 * Maintains backward compatibility with existing code.
 */
export const DeleteConfirmationDialog = <TExecuteParameters extends unknown[]>({ 
  open, 
  itemName, 
  title, 
  message, 
  onCancel, 
  onDeleteComplete, 
  request, 
  parameters 
}: {
  open: boolean;
  itemName: string;
  title?: string;
  message?: string;
  onCancel: () => void;
  onDeleteComplete: () => void;
  request: UseAsyncResult<TExecuteParameters, unknown>;
  parameters: TExecuteParameters;
}) => {
  const confirmationTitle = title || `Delete ${itemName}`;
  const confirmationMessage = message || `Are you sure you want to delete this ${itemName}?`;
  
  return (
    <ConfirmationDialog
      open={open}
      title={confirmationTitle}
      message={confirmationMessage}
      confirmLabel="Delete"
      confirmColor="red"
      itemName={itemName}
      successTitle="Deleted successfully"
      successMessage={`Successfully deleted ${itemName}.`}
      onCancel={onCancel}
      onActionComplete={onDeleteComplete}
      request={request}
      parameters={parameters}
    />
  );
};